/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import GroupProperties, {
  Results
} from '@ion/core/src/services/GroupProperties';
import {
  GroupCategoryEntity,
  GroupTypeEntity,
  InheritableProperty,
  InheritablePropertyValue
} from '@ion/core/src/types/entities';
import {
  alphaSort
} from '@ion/core/src/utils/SortUtils';
import {
  capitalizeFirstLetter
} from '@ion/core/src/utils/StringUtil';
import Dropdown, {
  createOptions,
  Options
} from '../components/menus/Dropdown';

/**
 * Defines the kind of Group Property Chooser that is to be used. GroupProperties
 * are currently supported for the types defined within this string enumeration.
 */
export enum GroupPropertyKind {
  Types = 'Types',
  Categories = 'Categories'
};

/**
 * Defines the group entity types which can be used as the EntityPropertyChooser
 * entity prop.
 */
export type GroupPropertyEntity = (
  GroupTypeEntity | GroupCategoryEntity
);

/**
 * Defines the exported prop types to be used by EntityPropertyChooser client
 * components which use the EntityPropertyChooser (e.g. EntityTypeChooser,
 * and EntityCategoryChooser).
 */
export interface EntityPropertyChooserClientProps {
  entity: GroupPropertyEntity;
  isLoading: boolean;
  defaultValue?: InheritablePropertyValue;
  readOnly?: boolean;
  onChange?: (type: InheritablePropertyValue) => void;
  values?: string[];
  includeGroupName?: boolean;
  labelFormatter?: (label: string, name: string) => string;
  required?: boolean;
  error?: string;
  classes?: string;
  hasError?: boolean;
  includeNone?: boolean;
  includeAll?: boolean;
  enableClear?: boolean;
  translation?: string;
  errorText?: string;
};

// defines the exported prop types for this component ...

/**
 * Defines the exported prop types for the EntityPropertyChooser component.
 * These props are primarily used only by both the EntityTypeChooser, and
 * EntityCategoryChooser components.
 */
export interface EntityPropertyChooserProps extends EntityPropertyChooserClientProps {
  kind: GroupPropertyKind;
  translation: string;
};

// defines the types arguments used by the getLabel heler ...
type GetLabelArgs = {
  kind: GroupPropertyKind;
  name: string;
  includeGroupName: boolean;
  translation: string;
};

/**
 * Generates the Options list of dropdown labels and values based on the current
 * GroupPropertyKind from the GroupProperties API.
 */
const getOptions = (kind: GroupPropertyKind, entity: GroupPropertyEntity): Options[] => {
  const method = GroupProperties[`getEntity${kind}`];
  const items  = method.call(GroupProperties, entity, true, false) as unknown as Results[] || [];
  return createOptions({items: alphaSort({items: items as string[]})});
};

/**
 * Determines the label to be displayed based on the entity name / type and
 * includeGroupName prop. When includeGroupName is true, the label will display
 * the Group name post-fixed in parens, otherwise, only the entity name will
 * be displayed.
 *
 * Applications can define both a label and labelGroup translation entries in
 * order to support these capabilities as follows:
 *
 * ```typescript
 *
 *  "entityType": {
 *    "label": "{{name}} Type",
 *    "labelGroup": "{{name}} Type (From {{group}})",
 *    "placeholder": "Select {{name}} Type"
 *  },
 *  "entityCategory": {
 *    "label": "{{name}} Category",
 *    "labelGroup": "{{name}} Category (From {{group}})",
 *    "placeholder": "Select {{name}} Category"
 *  }
 *
 * ```
 *
 */
const getLabel = ({kind, name, translation, includeGroupName}: GetLabelArgs): string => {
  if (includeGroupName) {
    const method = GroupProperties[`getEntity${kind}`];
    const entity: GroupPropertyEntity = name.toLowerCase() as GroupPropertyEntity;
    const {account} = method.call(GroupProperties, entity) as InheritableProperty;
    return translate(`${translation}.labelGroup`, {name, group: account.title});
  }
  return translate(`${translation}.label`, {name: translate(`common:entityLabels.${name.toLowerCase()}`)});
};

/**
 * Provides a utility which determines if the provided value is available within
 * the current list of entity types. This ensures that when new types are loaded,
 * the selection is reset if the currently selected value is not available within
 * the new list of items, while also ensuring that the value is reselected if it
 * is available.
 */
const getSelectedValue = (
  value: InheritablePropertyValue,
  options: Options[]
): InheritablePropertyValue => {
  const results = options.find(option => option.value === value);
  return results?.value || '';
};

// default styles used by the component when a classes prop is not provided ...
const useStyles = makeStyles(theme => ({
  root : {
    padding : '0px !important',
    margin  : '0px !important'
  }
}));

/**
 * @EntityPropertyChooser
 *
 * The EntityPropertyChooser provides the abstraction for displaying a list of user
 * defined, account specific entity categories and types for display within a dropdown
 * list. Generally, clients would not use the EntityPropertyChooser component directly,
 * but rather, would use one of the composed components which build on this component,
 * specifically, the EntityTypeChooser and EntityCategoryChooser components, respectively.
 *
 * @see  EntityTypeChooser
 * @see  EntityCategoryChooser
 * @see  EntityPropertyChooserClientProps
 * @see  EntityPropertyChooserProps
 */
export const EntityPropertyChooser: React.FC<EntityPropertyChooserProps>  = ({
  kind,
  translation,
  entity,
  isLoading,
  hasError,
  errorText,
  defaultValue,
  readOnly,
  onChange,
  labelFormatter,
  values,
  classes,
  required,
  includeGroupName = true,
  includeNone = true,
  includeAll = false,
  enableClear = false
}: EntityPropertyChooserProps) => {
  const _classes = useStyles();
  const items: Options[] = (values ? createOptions({items: values})
    : getOptions(kind, entity)
  );
  const [options, setOptions]   = useState<Options[]>(items);
  const [selected, setSelected] = useState<InheritablePropertyValue>('');
  const [isNoneSelected, setIsNoneSelected] = useState(false);

  const name: string    = capitalizeFirstLetter(entity) as string;
  const placeholderText = (name.toLowerCase() === GroupCategoryEntity.landmark)
    ? GroupCategoryEntity.geofence
    : name;

  const _label: string = getLabel({
    includeGroupName : includeGroupName && !isLoading,
    translation,
    kind,
    name
  });
  const label = labelFormatter ? labelFormatter(_label, name) : _label;
  const placeholder = translate(`common:entityLabels.${placeholderText.toLowerCase()}`);

  useEffect((): void => {
    if (!isLoading && !values) {
      const types = getOptions(kind, entity);
      setSelected(getSelectedValue(selected, types));
      if (includeAll) {
        const allLabel = translate('common:options.all');
        types.unshift({value: allLabel, label: allLabel});
        setSelected(getSelectedValue(allLabel, types));
      }
      setOptions(types);
    }
  }, [isLoading]);

  useEffect(() => {
    if (defaultValue && !selected) {
      setSelected(defaultValue);
    }
  }, [defaultValue]);

  return (
    <div className={classes || _classes.root}>
      <Dropdown displaySelectedLabel
        enableClear={enableClear}
        options={options}
        fieldLabel={label}
        hasError={hasError}
        disabled={!values && (readOnly || isLoading)}
        placeholder={isNoneSelected ? translate('common:options.none') : translate(`${translation}.placeholder`, {name: placeholder})}
        value={selected as unknown as string}
        preferPlaceholder={!isLoading && !selected}
        includeNone={includeNone}
        loading={isLoading}
        required={required}
        errorText={hasError && errorText}
        onChange={function({target}) {
          const val = target.value as InheritablePropertyValue;
          setSelected(getSelectedValue(val, options));
          onChange?.(val || undefined);
          val === 'None'? setIsNoneSelected(true) : setIsNoneSelected(false);
        }}
        name={name} />
    </div>
  );
};
export default EntityPropertyChooser;
